import { useCallback, useEffect, useState } from "react";
import Header from "@/components/Header";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import CancelModal from "@/components/CancelModal";
import RefundModal from "@/components/RefundModal";
import TrialTripwire from "@/components/TrialTripwire";
import HeroMetric from "@/components/HeroMetric";
import LeakList from "@/components/LeakList";
import ClassActionFeed from "@/components/ClassActionFeed";
import RefundTimeline from "@/components/RefundTimeline";
import GmailConnect from "@/components/GmailConnect";
import Footer from "@/components/Footer";
import DeleteAccount from "@/components/DeleteAccount";
import VerifyEmailGate from "@/components/VerifyEmailGate";
import PaywallModal from "@/components/PaywallModal";
import ShareCardModal from "@/components/ShareCardModal";
import LandedRefunds from "@/components/LandedRefunds";
import InsightsCard from "@/components/InsightsCard";
import DemoBanner from "@/components/DemoBanner";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ScanLine } from "lucide-react";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const subscribed = !!user?.subscription_active;
  const [leaks, setLeaks] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [classActions, setClassActions] = useState([]);
  const [stats, setStats] = useState(null);
  const [gmail, setGmail] = useState({ connected: false, credentials_available: true, live_leaks: 0 });
  const [cancelTarget, setCancelTarget] = useState(null);
  const [refundTarget, setRefundTarget] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const [params, setParams] = useSearchParams();

  // Paid actions: open the paywall instead when not subscribed
  const gated = useCallback((fn) => (...args) => (subscribed ? fn(...args) : setShowPaywall(true)), [subscribed]);
  const openCancel = gated(setCancelTarget);
  const openRefund = gated(setRefundTarget);

  useEffect(() => {
    const onPaywall = () => setShowPaywall(true);
    window.addEventListener("clawback:paywall", onPaywall);
    return () => window.removeEventListener("clawback:paywall", onPaywall);
  }, []);

  useEffect(() => {
    if (!user || user.email_verified === false || subscribed || params.get("gmail") || params.get("billing")) return;
    if (sessionStorage.getItem("clawback_paywall_seen")) return;
    sessionStorage.setItem("clawback_paywall_seen", "1");
    setShowPaywall(true);
  }, [user, subscribed, params]);

  const load = useCallback(async () => {
    try {
      const [l, r, ca, s, g] = await Promise.all([
        api.get("/leaks"),
        api.get("/refunds"),
        api.get("/class-actions"),
        api.get("/stats/hero"),
        api.get("/gmail/status"),
      ]);
      setLeaks(l.data);
      setRefunds(r.data);
      setClassActions(ca.data);
      setStats(s.data);
      setGmail(g.data);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error("Couldn't load your dashboard — retrying shortly");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const scanNow = useCallback(async () => {
    try {
      const { data } = await api.post("/gmail/scan");
      if (data.mode === "live") {
        const parts = [`${data.scanned} receipts scanned`];
        if (data.found > 0) parts.push(`${data.found} new real leak${data.found > 1 ? "s" : ""} flagged`);
        else parts.push("no new leaks");
        if (data.demo_removed > 0) parts.push(`demo data replaced`);
        if (data.partial) parts.push("more to scan — run again");
        toast.success(parts.join(" · "));
      } else if (data.mode === "error") {
        toast.error(`Scan failed: ${data.error || "try again in a moment"}`);
      } else {
        toast.success(`Scanned ${data.scanned} receipts · ${data.found} leaks flagged`);
      }
      await load();
      return data;
    } catch {
      toast.error("Scan failed");
    }
  }, [load]);

  useEffect(() => {
    if (params.get("gmail") === "connected") {
      toast.success("Gmail connected — running your first real scan…");
      setParams({});
      refresh();
      // give the DB a beat to persist gmail_connected then scan
      const t = setTimeout(() => { scanNow(); }, 400);
      return () => clearTimeout(t);
    }
    if (params.get("billing") === "updated") {
      toast.success("Billing updated");
      setParams({});
      refresh();
    }
    if (params.get("gmail") === "state_error") {
      toast.error("Gmail connect failed — please try again");
      setParams({});
    }
  }, [params, setParams, refresh, scanNow]);

  const trialLeak = leaks.find((x) => x.trial && x.status === "active");

  return (
    <div className="min-h-screen">
      <Header inApp />
      {user && user.email_verified === false ? <VerifyEmailGate /> : (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="dashboard-root">
        {/* Trial Tripwire */}
        {trialLeak && <TrialTripwire leak={trialLeak} onCancel={() => openCancel(trialLeak)} onRefund={() => openRefund(trialLeak)} />}

        {/* Preview / demo notice */}
        <DemoBanner gmailConnected={gmail.connected} subscribed={subscribed} leaks={leaks} onUpgrade={() => setShowPaywall(true)} />

        {/* Hero Metric */}
        <HeroMetric stats={stats} onUpgrade={() => setShowPaywall(true)} subscriptionActive={subscribed} />

        {/* Claude nightly brief */}
        <InsightsCard leaks={leaks} onCancel={openCancel} onRefund={openRefund} />

        {/* Grid */}
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl font-bold tracking-tight" data-testid="leaks-heading">Your subscription leaks</h2>
              <Button onClick={scanNow} variant="outline" className="btn-outline-paper" data-testid="scan-now-button">
                <ScanLine className="h-4 w-4 mr-1.5" /> Scan inbox
              </Button>
            </div>
            <LeakList leaks={leaks} onCancel={openCancel} onRefund={openRefund} />

            <RefundTimeline refunds={refunds} onLoad={load} onLanded={(r) => setShareTarget(r)} />

            <LandedRefunds refunds={refunds} onShare={(r) => setShareTarget(r)} />
          </div>

          <div className="lg:col-span-4 space-y-6">
            <GmailConnect status={gmail} onScan={scanNow} onChange={load} />
            <ClassActionFeed items={classActions} onClaim={gated(async (id) => {
              try {
                const { data } = await api.post(`/class-actions/${id}/claim`);
                if (data.claim_url) window.open(data.claim_url, "_blank", "noopener");
                toast.success("Claim form opened — file it there and we'll track the payout.");
                load();
              } catch (e) {
                if (e?.response?.status !== 402) toast.error(e?.response?.data?.detail || "Couldn't file claim");
              }
            })} />
          </div>
        </div>
        <DeleteAccount />
      </main>
      )}
      <Footer />

      {cancelTarget && (
        <CancelModal
          leak={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onConfirm={async (strategy) => {
            try {
              const { data } = await api.post("/leaks/cancel", { leak_id: cancelTarget.id });
              const path = strategy?.best_path;
              if (["email", "chat", "phone", "bank"].includes(path)) {
                toast.success(`${cancelTarget.merchant} marked cancelled — follow the ${path} script`);
              } else {
                toast.success(`Deep-linked to ${cancelTarget.platform.toUpperCase()} to finish cancel`);
                if (data.cancel_url) window.open(data.cancel_url, "_blank", "noopener");
              }
              setCancelTarget(null);
              load();
            } catch (e) {
              if (e?.response?.status !== 402) toast.error(e?.response?.data?.detail || "Couldn't cancel");
            }
          }}
        />
      )}
      {refundTarget && (
        <RefundModal
          leak={refundTarget}
          onClose={() => setRefundTarget(null)}
          onDone={() => { setRefundTarget(null); load(); }}
        />
      )}
      {showPaywall && <PaywallModal onClose={() => setShowPaywall(false)} />}
      {shareTarget && (
        <ShareCardModal
          refund={shareTarget}
          onClose={() => setShareTarget(null)}
        />
      )}
    </div>
  );
}
